type Listener = (...args: any[]) => void;

interface ListenerEntry {
  fn: Listener;
  context: unknown;
  once: boolean;
}

/**
 * Typed event emitter with zero dependencies.
 *
 * Usage:
 * ```ts
 * interface MyEvents {
 *   'foo': [x: number, y: string];
 *   'bar': [];
 * }
 * const emitter = new EventEmitter<MyEvents>();
 * emitter.on('foo', (x, y) => console.log(x, y));
 * emitter.emit('foo', 42, 'hello');
 * ```
 */
export class EventEmitter<TEvents extends Record<string, unknown[]>> {
  private _listeners = new Map<keyof TEvents, ListenerEntry[]>();

  on<K extends keyof TEvents>(
    event: K,
    fn: (...args: TEvents[K]) => void,
    context?: unknown,
  ): this {
    return this._add(event, fn as Listener, context, false);
  }

  once<K extends keyof TEvents>(
    event: K,
    fn: (...args: TEvents[K]) => void,
    context?: unknown,
  ): this {
    return this._add(event, fn as Listener, context, true);
  }

  off<K extends keyof TEvents>(
    event: K,
    fn?: (...args: TEvents[K]) => void,
    context?: unknown,
  ): this {
    const entries = this._listeners.get(event);
    if (!entries) return this;

    if (!fn) {
      this._listeners.delete(event);
      return this;
    }

    const filtered = entries.filter(
      (e) => e.fn !== fn || (context !== undefined && e.context !== context),
    );
    if (filtered.length === 0) {
      this._listeners.delete(event);
    } else {
      this._listeners.set(event, filtered);
    }
    return this;
  }

  emit<K extends keyof TEvents>(event: K, ...args: TEvents[K]): boolean {
    const entries = this._listeners.get(event);
    if (!entries || entries.length === 0) return false;

    // Snapshot to allow mutations during iteration
    const snapshot = entries.slice();
    for (const entry of snapshot) {
      if (entry.once) {
        // Remove this specific entry by identity. Calling off(fn, context)
        // would drop *every* listener with the same fn reference — including a
        // separate persistent on() registration of the same handler.
        this._removeEntry(event, entry);
      }
      entry.fn.apply(entry.context, args);
    }
    return true;
  }

  private _removeEntry(event: keyof TEvents, entry: ListenerEntry): void {
    const entries = this._listeners.get(event);
    if (!entries) return;
    const idx = entries.indexOf(entry);
    if (idx === -1) return;
    entries.splice(idx, 1);
    if (entries.length === 0) this._listeners.delete(event);
  }

  removeAllListeners(event?: keyof TEvents): this {
    if (event !== undefined) {
      this._listeners.delete(event);
    } else {
      this._listeners.clear();
    }
    return this;
  }

  listenerCount(event: keyof TEvents): number {
    return this._listeners.get(event)?.length ?? 0;
  }

  private _add(
    event: keyof TEvents,
    fn: Listener,
    context: unknown,
    once: boolean,
  ): this {
    let entries = this._listeners.get(event);
    if (!entries) {
      entries = [];
      this._listeners.set(event, entries);
    }
    entries.push({ fn, context, once });
    return this;
  }
}
